/**
 * How the director finds nodes.
 *
 * Query selectors against one registered root, not refs threaded through six
 * components. The table already carries a stable attribute contract for the
 * e2e suite — `data-testid`, `data-card-id`, `data-flip-card`, `data-seat-clock`
 * — and the timelines read exactly the same attributes. One contract, two
 * consumers; a rename breaks both at once instead of breaking the animations
 * silently.
 *
 * Every reader here is total. A timeline that cannot find its nodes gets an
 * empty array and finishes immediately, which is the correct failure: a missing
 * animation, never a dead table.
 */

import type { CardId, Seat } from '@ordago/rules';

/**
 * Nothing in this module imports GSAP, deliberately. `bindRoom` reaches the
 * director and the event translator, which reach here — so a single value
 * import of GSAP anywhere on that path would pull the whole library into the
 * entry chunk and make the login screen pay for the table's animations.
 */

let root: HTMLElement | null = null;

/** `.table-surface` — the element every HUD overlay and the grid live inside. */
export function setTableRoot(el: HTMLElement | null): void {
  root = el;
}

export function tableRoot(): HTMLElement | null {
  return root;
}

export function q<T extends HTMLElement>(selector: string): T | null {
  return root?.querySelector<T>(selector) ?? null;
}

export function qa<T extends HTMLElement>(selector: string): T[] {
  return root === null ? [] : Array.from(root.querySelectorAll<T>(selector));
}

// ────────────────────────────────────────────────────────────────── table parts

export function deckEl(): HTMLElement | null {
  return q('[data-testid="deck-stack"]');
}

export function discardEl(): HTMLElement | null {
  return q('[data-testid="discard-pile"]');
}

export function potEl(): HTMLElement | null {
  return q('[data-testid="pot-chips"]') ?? q('[data-testid="center-zone"]');
}

export function seatEl(seat: Seat): HTMLElement | null {
  return q(`[data-testid="seat-${String(seat)}"]`);
}

export function scoreColumnEl(side: 'us' | 'them'): HTMLElement | null {
  return q(`[data-testid="score-${side}"]`);
}

/**
 * A seat's card nodes, in fan order. The own hand has its own test id because
 * it is the one hand whose cards carry real identities.
 */
export function handCards(seat: Seat, mySeat: Seat | null): HTMLElement[] {
  const selector =
    seat === mySeat
      ? '[data-testid="hand-own"] .card'
      : `[data-testid="hand-${String(seat)}"] .card`;
  return qa(selector);
}

/** A specific card by identity — only ever findable for cards this client may see. */
export function cardById(id: CardId): HTMLElement | null {
  return q(`[data-card-id="${CSS.escape(id)}"]`);
}

/** The absolutely positioned, pointer-transparent layer flights are drawn on. */
export function flightLayer(): HTMLElement | null {
  return q('[data-testid="flight-layer"]');
}

// ─────────────────────────────────────────────────────────────────── geometry

export interface Point {
  x: number;
  y: number;
}

export function centerOf(el: Element): Point {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * The transform that would put `el` where `from` is, expressed as GSAP's
 * `x`/`y`/`scale`. This is the "invert" half of a FLIP done by hand, and it is
 * what lets a card fly in from the deck without the deck and the card ever
 * having shared a DOM parent.
 *
 * It composes with the fan because the fan is written with the individual
 * `translate`/`rotate` CSS properties, which the CSS transform model applies
 * *before* `transform` — so GSAP owns `transform` outright and never has to
 * reproduce the fan geometry it would otherwise clobber.
 */
export function invertFrom(
  el: HTMLElement,
  from: DOMRect,
): { x: number; y: number; scale: number } {
  const to = el.getBoundingClientRect();
  if (to.width === 0 || from.width === 0) return { x: 0, y: 0, scale: 1 };
  return {
    x: from.left + from.width / 2 - (to.left + to.width / 2),
    y: from.top + from.height / 2 - (to.top + to.height / 2),
    scale: from.width / to.width,
  };
}

/**
 * A quadratic arc between two points with a single control point lifted above
 * the midpoint — the shape §3.4 #3 and #6 both ask for, in the coordinate space
 * `MotionPathPlugin` wants (relative to the tweened element's start).
 */
export function arc(from: Point, to: Point, lift = 0.32): Point[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const span = Math.hypot(dx, dy);
  return [
    { x: 0, y: 0 },
    { x: dx / 2, y: dy / 2 - span * lift },
    { x: dx, y: dy },
  ];
}

// ────────────────────────────────────────────────────────── will-change budget

/**
 * `will-change` is applied by the director on timeline start and removed on
 * complete, never left in a stylesheet (§3.5). A permanent `will-change` on 24
 * cards keeps 24 compositor layers alive for the whole match, which costs more
 * than the promotion saves.
 *
 * Written straight to the inline style rather than through `gsap.set`: it is one
 * property with no easing, and doing it by hand is what keeps this module free
 * of a GSAP import.
 */
export function promote(els: Element[]): void {
  for (const el of els) {
    if (el instanceof HTMLElement) el.style.willChange = 'transform';
  }
}

export function clearWillChange(els: Element[]): void {
  for (const el of els) {
    if (el instanceof HTMLElement) el.style.willChange = '';
  }
}

// ──────────────────────────────────────────────────────────────────── waiting

/** Resolves after the browser has painted, so a React commit is on screen. */
export function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
}

/**
 * Polls a predicate once per frame up to `timeoutMs`, then gives up.
 *
 * The schema patch that grows a hand and the `game.event` that says why arrive
 * on two independent channels, so a timeline cannot assume its nodes exist the
 * instant it is dequeued. Giving up quietly is the whole point: the animation is
 * skipped, the game is not.
 */
export async function waitFor(predicate: () => boolean, timeoutMs = 600): Promise<boolean> {
  const deadline = performance.now() + timeoutMs;
  while (performance.now() < deadline) {
    if (predicate()) return true;
    await nextFrame();
  }
  return predicate();
}
